import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-12 bg-charcoal">
      <div className="w-full max-w-sm">
        <div className="mb-8"><Link to="/"><Logo /></Link></div>
        <div className="label-mono mb-2">Reset password</div>
        <h1 className="font-display text-3xl font-bold text-white mb-4">Forgot your password?</h1>

        {sent ? (
          <div className="card-surface rounded-md p-5" data-testid="forgot-sent">
            <p className="text-sm text-white/75 leading-relaxed">
              If an account exists for <span className="text-gold">{email}</span>, we've emailed a link to reset your password.
              Check your inbox (and spam). The link expires in 1 hour.
            </p>
            <Link to="/login" className="mt-4 inline-block text-sm text-gold hover:text-gold-hover" data-testid="back-to-login-link">← Back to sign in</Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-white/50 mb-6">Enter your email and we'll send you a link to set a new password.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="label-mono">Email</Label>
                <Input data-testid="forgot-email-input" type="email" required autoComplete="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 bg-charcoal border-white/15 focus-visible:ring-gold text-white" placeholder="you@example.com" />
              </div>
              <Button type="submit" disabled={loading} data-testid="forgot-submit-btn"
                className="w-full bg-gold text-charcoal hover:bg-gold-hover font-semibold rounded-sm">
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </form>
            <p className="mt-6 text-sm text-white/50 text-center">
              Remembered it? <Link to="/login" className="text-gold hover:text-gold-hover" data-testid="back-to-login-link">Sign in</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
